import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Notificaciones ya leidas del portal Legal Flow.
 *
 * Las notificaciones son DERIVADAS (se calculan de cuentas, documentos y
 * bitacora, ver useNotificacionesLegalFlow): no hay fila que marcar en BD. El
 * estado de leido vivia en un `useState` dentro de la pantalla, asi que marcar
 * todas como leidas no bajaba el contador de la campana del header -que lee el
 * total- y se perdia al navegar.
 *
 * Aqui es un store persistido en `localStorage`, de modo que la campana y la
 * pantalla cuentan lo mismo y la marca sobrevive la navegacion y el refresco.
 *
 * Limitacion conocida: es por navegador. Para que siga al usuario entre
 * dispositivos hace falta una tabla (`legal_flow_notificaciones_leidas`), que va
 * como cambio de BD aparte.
 */

export const CLAVE_NOTIFICACIONES_LEIDAS = 'legal-flow:notificaciones-leidas';

interface EstadoLeidas {
  /** Ids de notificacion marcados como leidos. */
  ids: string[];
  marcarLeida: (id: string) => void;
  marcarVarias: (ids: string[]) => void;
  reiniciar: () => void;
}

export const useNotificacionesLeidasStore = create<EstadoLeidas>()(
  persist(
    (set) => ({
      ids: [],
      marcarLeida: (id) =>
        set((s) => (s.ids.includes(id) ? s : { ids: [...s.ids, id] })),
      marcarVarias: (ids) =>
        set((s) => ({ ids: Array.from(new Set([...s.ids, ...ids])) })),
      reiniciar: () => set({ ids: [] }),
    }),
    { name: CLAVE_NOTIFICACIONES_LEIDAS },
  ),
);

/** Helpers listos para la pantalla y para la campana del header. */
export function useNotificacionesLeidas() {
  const ids = useNotificacionesLeidasStore((s) => s.ids);
  const marcarLeida = useNotificacionesLeidasStore((s) => s.marcarLeida);
  const marcarVarias = useNotificacionesLeidasStore((s) => s.marcarVarias);
  const leidas = new Set(ids);
  return {
    leidas,
    estaLeida: (id: string) => leidas.has(id),
    /** Cuantas de la lista dada siguen sin leer. */
    sinLeer: (todas: { id: string }[]) => todas.filter((n) => !leidas.has(n.id)).length,
    marcarLeida,
    marcarVarias,
  };
}
